import getFetcher from 'api/utils/getFetcher';
import { useMemo } from 'react';
import useSWR, { useSWRConfig, type SWRConfiguration } from 'swr';

export interface OpenSessionsResult {
  success: boolean;
  data: { deviceids: Array<DeviceId> };
}

export interface OpenSessionsError {}

type SimsSWRConfiguration = SWRConfiguration<OpenSessionsResult, OpenSessionsError>;

const useOpenSessions = (organizationId?: OrgId, canLoad = false) => {
  const queryParams = new URLSearchParams({ orgid: `${organizationId}` });
  const url = organizationId
    ? `${process.env.VITE_API_ROOT}/devices/opensessions?${queryParams}`
    : `${process.env.VITE_API_ROOT}/devices/opensessions`;

  const config: SimsSWRConfiguration = useSWRConfig();

  const openSessionsConfig = useMemo(
    () => ({
      ...config,
      dedupingInterval: 5000,
      focusThrottleInterval: 5000,
      refreshInterval: 30000,
    }),
    [config]
  );

  const { data, error, isLoading, mutate, isValidating } = useSWR<
    OpenSessionsResult,
    OpenSessionsError
  >(canLoad ? url : null, getFetcher, openSessionsConfig);

  const deviceIdsWithOpenSessions = useMemo(() => new Set(data?.data?.deviceids ?? []), [data]);

  return {
    deviceIdsWithOpenSessions,
    error,
    isLoading,
    mutate,
    isValidating,
  };
};

export default useOpenSessions;
